<template>
  <div class="entities-page" :class="{ 'entities-page-rtl': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="entities-page-intro">
      <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">{{ t('entities.title', 'Entities') }}</h1>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ t('entities.subtitle', 'View conversation entities used for external analysis, such as agents, teams, and CRM groups.') }}</p>
    </div>

    <div role="tablist" class="tabs tabs-lifted entities-page-tabs-shell" :dir="isRtl === true ? 'rtl' : 'ltr'">
      <input
        type="radio"
        name="entities_management_tabs"
        role="tab"
        class="tab"
        :aria-label="t('entities.tabs.entities', 'Entities')"
        :checked="activeTab === 'entities'"
        @change="selectTab('entities')"
      />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4">
        <div class="entities-header">
          <div class="entities-header-intro">
            <h2 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">{{ t('entities.entitiesTabTitle', 'Manage entities') }}</h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ t('entities.entitiesTabSubtitle', 'Create and review the entities used for analysis and reporting.') }}</p>
          </div>
          <button class="btn" type="button" @click="openCreateModal">
            {{ t('entities.createButton', 'Create entity') }}
          </button>
        </div>

        <div class="entities-layout">
      <div class="entities-list-card bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow">
        <div class="entities-list-toolbar">
          <label class="input input-bordered flex items-center entities-search-input">
            <input
              v-model="searchValue"
              type="text"
              class="grow"
              maxlength="120"
              autocomplete="off"
              :placeholder="t('entities.searchPlaceholder', 'Search entities')"
            />
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
              <path fill-rule="evenodd" d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0a3.5 3.5 0 0 1 7 0Z" clip-rule="evenodd" />
            </svg>
          </label>
        </div>
        <div v-if="isLoading === true" class="entities-state">
          <span class="loading loading-spinner loading-lg text-indigo-600"></span>
        </div>
        <div v-else-if="entities.length === 0" class="entities-state">
          {{ t('entities.empty', 'No entities yet. Create one to analyze real-world conversations.') }}
        </div>
        <div v-else-if="filteredEntities.length === 0" class="entities-state">
          {{ t('entities.searchEmpty', 'No entities match your search.') }}
        </div>
        <div v-else ref="listScrollRef" class="entities-list-scroll" @scroll="handleListScroll">
          <div class="entities-list">
          <button
            v-for="entity in visibleEntities"
            :key="entity.entityId"
            type="button"
            class="entities-list-item"
            :class="{ 'entities-list-item-active': selectedEntityId === entity.entityId }"
            @click="selectEntity(entity.entityId)"
          >
            <div class="entities-list-item-icon">
              <Icon :icon="resolveEntityIcon(entity.icon)" width="22" height="22" />
            </div>
            <div class="entities-list-item-copy">
              <div class="entities-list-item-title-row">
                <span class="font-semibold text-gray-900 dark:text-gray-100">{{ getEntityDisplayName(entity) }}</span>
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ getEntityTypeLabel(entity) }}</div>
            </div>
          </button>
          </div>
          <div v-if="hasMoreEntitiesToShow === true" class="entities-list-loading-more">
            <span class="loading loading-spinner loading-sm text-indigo-600"></span>
            <span>{{ t('entities.loadingMore', 'Loading more entities') }}</span>
          </div>
        </div>
      </div>

      <div class="entities-detail-card bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow">
        <div v-if="selectedEntity === null" class="entities-state">
          {{ t('entities.selectHint', 'Select an entity to view its details.') }}
        </div>
        <div v-else class="entities-detail">
          <div class="entities-detail-header">
            <div class="entities-detail-icon">
              <Icon :icon="resolveEntityIcon(selectedEntity.icon)" width="32" height="32" />
            </div>
            <div class="entities-detail-heading">
              <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ getEntityDisplayName(selectedEntity) }}</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ getEntityTypeLabel(selectedEntity) }}</p>
            </div>
            <div class="entities-detail-actions">
              <router-link
                :to="{ path: '/analysisEngine', query: { target: 'entities', entityId: selectedEntity.entityId } }"
                class="btn btn-sm"
              >
                {{ t('entities.configureAnalysis', 'Configure analysis') }}
              </router-link>
              <button
                v-if="canDeleteEntity(selectedEntity) === true"
                type="button"
                class="btn btn-sm border-transparent bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900 entities-detail-delete-btn"
                @click="openDeleteModal"
              >
                {{ t('entities.deleteButton', 'Delete entity') }}
              </button>
            </div>
          </div>

          <div class="entities-detail-grid">
            <div class="entities-detail-field">
              <div class="entities-detail-label">{{ t('entities.fields.entityId', 'Entity ID') }}</div>
              <div class="entities-detail-value entities-detail-mono">{{ selectedEntity.entityId }}</div>
            </div>
            <div class="entities-detail-field">
              <div class="entities-detail-label">{{ t('entities.fields.entityType', 'Type key') }}</div>
              <div class="entities-detail-value">{{ selectedEntity.entityType }}</div>
            </div>
            <div class="entities-detail-field">
              <div class="entities-detail-label">{{ t('entities.fields.icon', 'Icon') }}</div>
              <div class="entities-detail-value">{{ selectedEntity.icon !== '' ? selectedEntity.icon : t('entities.noIcon', '—') }}</div>
            </div>
            <div class="entities-detail-field">
              <div class="entities-detail-label">{{ t('entities.fields.analysis', 'Analysis engine') }}</div>
              <div class="entities-detail-value">
                <span v-if="isAnalysisEnabled(selectedEntity) === true" class="badge badge-success badge-sm">{{ t('entities.analysisEnabled', 'Enabled') }}</span>
                <span v-else class="badge badge-ghost badge-sm">{{ t('entities.analysisDisabled', 'Not configured') }}</span>
              </div>
            </div>
          </div>

          <div class="entities-localized-section">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('entities.namesTitle', 'Names by language') }}</h3>
            <div class="entities-localized-list">
              <div v-for="entry in getLocalizedEntries(selectedEntity.name)" :key="`name-${entry.code}`" class="entities-localized-row">
                <span class="entities-localized-code">{{ entry.code }}</span>
                <span class="entities-localized-text">{{ entry.label }}</span>
              </div>
              <div v-if="getLocalizedEntries(selectedEntity.name).length === 0" class="text-sm text-gray-500 dark:text-gray-400">{{ t('entities.noLocalizedValues', 'No localized values.') }}</div>
            </div>
          </div>

          <div class="entities-localized-section">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('entities.typeLabelsTitle', 'Type labels by language') }}</h3>
            <div class="entities-localized-list">
              <div v-for="entry in getLocalizedEntries(selectedEntity.typeLabels)" :key="`type-${entry.code}`" class="entities-localized-row">
                <span class="entities-localized-code">{{ entry.code }}</span>
                <span class="entities-localized-text">{{ entry.label }}</span>
              </div>
              <div v-if="getLocalizedEntries(selectedEntity.typeLabels).length === 0" class="text-sm text-gray-500 dark:text-gray-400">{{ t('entities.noLocalizedValues', 'No localized values.') }}</div>
            </div>
          </div>
        </div>
      </div>
        </div>

        <AppModal :visible="createModalOpen === true" :title="t('entities.createModalTitle', 'Create entity')" boxClass="max-w-2xl w-11/12 entities-create-modal-box" @close="closeCreateModal">
          <div class="p-2 entities-modal-content" :class="{ 'entities-modal-content-rtl': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
            <div class="entities-form-grid">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">{{ t('entities.form.nameEn', 'Name (English)') }}</span>
                </label>
                <input v-model="createDraft.nameEn" class="input input-bordered w-full" maxlength="120" />
              </div>
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">{{ t('entities.form.nameHe', 'Name (Hebrew)') }}</span>
                </label>
                <input v-model="createDraft.nameHe" class="input input-bordered w-full" maxlength="120" />
              </div>
            </div>
            <div class="entities-form-grid mt-3">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">{{ t('entities.form.entityId', 'Entity ID') }}</span>
                </label>
                <input v-model="createDraft.entityId" class="input input-bordered w-full" maxlength="255" :placeholder="t('entities.form.entityIdPlaceholder', 'Leave empty to auto-generate')" />
              </div>
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">{{ t('entities.form.entityType', 'Entity type') }}</span>
                </label>
                <input
                  :value="createDraft.entityType"
                  class="input input-bordered w-full"
                  maxlength="80"
                  :placeholder="t('entities.form.entityTypePlaceholder', 'agent')"
                  @input="onEntityTypeChange($event.target.value)"
                />
              </div>
            </div>
            <div class="form-control mt-3">
              <label class="label">
                <span class="label-text font-medium">{{ t('entities.form.entityTypeSelect', 'Choose type preset') }}</span>
              </label>
              <EntityTypeSelector
                :selected-entity-type="createDraft.entityType"
                @update:selected-entity-type="onEntityTypeChange"
                @preset-selected="onEntityPresetSelected"
              />
            </div>
            <div class="form-control mt-3">
              <label class="label">
                <span class="label-text font-medium">{{ t('entities.form.icon', 'Icon') }}</span>
              </label>
              <EntityIconSelector
                :selected-icon="createDraft.icon"
                @update:selected-icon="onEntityIconChange"
              />
            </div>
          </div>
          <template #actions>
            <div class="entities-modal-actions" :class="{ 'entities-modal-actions-rtl': isRtl === true }">
              <button type="button" class="btn" @click="closeCreateModal">{{ t('entities.cancelButton', 'Cancel') }}</button>
              <button type="button" class="btn" :disabled="isCreating === true" @click="createEntity">
                <span v-if="isCreating === true" class="loading loading-spinner loading-xs entities-save-spinner"></span>
                {{ t('entities.saveButton', 'Create') }}
              </button>
            </div>
          </template>
        </AppModal>

        <AppModal
          :visible="deleteModalOpen === true"
          :title="t('entities.deleteModalTitle', 'Delete entity')"
          boxClass="max-w-lg w-11/12"
          @close="closeDeleteModal"
        >
          <div class="p-4" :class="{ 'text-right': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ t('entities.deleteModalMessage', 'Are you sure you want to delete this entity? Related conversations will also be removed.') }}
            </p>
            <p v-if="entityPendingDelete !== null" class="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-3">
              {{ getEntityDisplayName(entityPendingDelete) }}
            </p>
            <div class="entities-delete-modal-actions mt-6" :class="{ 'entities-delete-modal-actions-rtl': isRtl === true }">
              <button type="button" class="btn" :disabled="isDeleting === true" @click="closeDeleteModal">
                {{ t('entities.cancelButton', 'Cancel') }}
              </button>
              <button type="button" class="btn border-transparent bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900" :disabled="isDeleting === true" @click="deleteEntity">
                <span v-if="isDeleting === true" class="loading loading-spinner loading-xs entities-delete-spinner"></span>
                {{ t('entities.deleteConfirmButton', 'Delete') }}
              </button>
            </div>
          </div>
        </AppModal>
      </div>

      <input
        type="radio"
        name="entities_management_tabs"
        role="tab"
        class="tab"
        :aria-label="t('entities.tabs.collections', 'Collections')"
        :checked="activeTab === 'collections'"
        @change="selectTab('collections')"
      />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4">
        <EntityCollectionsManager :entities="entities" :is-rtl="isRtl" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import AppModal from '@/components/AppModal.vue';
import EntityTypeSelector from '@/components/EntityTypeSelector.vue';
import EntityIconSelector from '@/components/EntityIconSelector.vue';
import EntityCollectionsManager from '@/components/EntityCollectionsManager.vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { findEntityTypePreset, normalizeEntityTypeKey, resolveEntityIconValue } from '@/shared/entityCatalog.js';

const { t, currentLanguage } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const entities = ref([]);
const selectedEntityId = ref('');
const isLoading = ref(false);
const isCreating = ref(false);
const isDeleting = ref(false);
const searchValue = ref('');
const listScrollRef = ref(null);
const visibleEntitiesCount = ref(30);
const createModalOpen = ref(false);
const deleteModalOpen = ref(false);
const entityPendingDelete = ref(null);
const createDraft = ref({
  entityId: '',
  entityType: '',
  icon: 'tabler:headset',
  nameEn: '',
  nameHe: '',
  typeEn: '',
  typeHe: ''
});

const isRtl = computed(() => {
  if (currentLanguage.value === 'he') {
    return true;
  }
  if (currentLanguage.value === 'ar') {
    return true;
  }
  return false;
});

const activeTab = computed(() => {
  const routeTab = normalizeStringValue(route.query?.tab).toLowerCase();
  if (routeTab === 'collections') {
    return 'collections';
  }
  return 'entities';
});

const selectedEntity = computed(() => {
  if (selectedEntityId.value === '') {
    return null;
  }
  const list = Array.isArray(filteredEntities.value) === true ? filteredEntities.value : [];
  for (let index = 0; index < list.length; index = index + 1) {
    if (list[index].entityId === selectedEntityId.value) {
      return list[index];
    }
  }
  return null;
});

const filteredEntities = computed(() => {
  const source = Array.isArray(entities.value) === true ? entities.value : [];
  const query = normalizeStringValue(searchValue.value).toLowerCase();
  if (query === '') {
    return source;
  }
  const result = [];
  for (let index = 0; index < source.length; index = index + 1) {
    const entity = source[index];
    const entityId = normalizeStringValue(entity?.entityId).toLowerCase();
    const entityType = normalizeStringValue(entity?.entityType).toLowerCase();
    const displayName = normalizeStringValue(getEntityDisplayName(entity)).toLowerCase();
    const typeLabel = normalizeStringValue(getEntityTypeLabel(entity)).toLowerCase();
    if (entityId.includes(query) === true || entityType.includes(query) === true || displayName.includes(query) === true || typeLabel.includes(query) === true) {
      result.push(entity);
    }
  }
  return result;
});

const visibleEntities = computed(() => {
  const source = Array.isArray(filteredEntities.value) === true ? filteredEntities.value : [];
  return source.slice(0, visibleEntitiesCount.value);
});

const hasMoreEntitiesToShow = computed(() => {
  return visibleEntities.value.length < filteredEntities.value.length;
});

function normalizeStringValue(value) {
  if (typeof value === 'string') {
    return value.trim();
  }
  return '';
}

function getAuthToken() {
  const authTokenRow = document.cookie.split('; ').find(row => row.startsWith('authToken=') !== false);
  const jwtTokenRow = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false);
  const authToken = authTokenRow !== undefined ? authTokenRow.split('=')[1] : '';
  const jwtToken = jwtTokenRow !== undefined ? jwtTokenRow.split('=')[1] : '';
  if (authToken !== '') {
    return authToken;
  }
  return jwtToken;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getLanguageCode() {
  if (currentLanguage.value === 'he') {
    return 'he';
  }
  return 'en';
}

function getEntityDisplayName(entity) {
  if (entity === null) {
    return '';
  }
  if (entity === undefined) {
    return '';
  }
  const languageCode = getLanguageCode();
  if (entity.name !== undefined && entity.name !== null && typeof entity.name === 'object') {
    if (typeof entity.name[languageCode] === 'string' && entity.name[languageCode].trim() !== '') {
      return entity.name[languageCode].trim();
    }
    if (typeof entity.name.en === 'string' && entity.name.en.trim() !== '') {
      return entity.name.en.trim();
    }
  }
  if (typeof entity.entityType === 'string' && entity.entityType.trim() !== '') {
    return entity.entityType.trim();
  }
  return entity.entityId;
}

function getEntityTypeLabel(entity) {
  if (entity === null) {
    return '';
  }
  if (entity === undefined) {
    return '';
  }
  const languageCode = getLanguageCode();
  if (entity.typeLabels !== undefined && entity.typeLabels !== null && typeof entity.typeLabels === 'object') {
    if (typeof entity.typeLabels[languageCode] === 'string' && entity.typeLabels[languageCode].trim() !== '') {
      return entity.typeLabels[languageCode].trim();
    }
    if (typeof entity.typeLabels.en === 'string' && entity.typeLabels.en.trim() !== '') {
      return entity.typeLabels.en.trim();
    }
  }
  if (typeof entity.entityType === 'string' && entity.entityType.trim() !== '') {
    return entity.entityType.trim();
  }
  return '';
}

function resolveEntityIcon(iconValue) {
  return resolveEntityIconValue(iconValue);
}

function onEntityTypeChange(value) {
  createDraft.value.entityType = typeof value === 'string' ? value : '';
  const preset = findEntityTypePreset(createDraft.value.entityType);
  if (preset === null) {
    const customKey = normalizeEntityTypeKey(createDraft.value.entityType);
    if (customKey !== '') {
      createDraft.value.typeEn = customKey;
      createDraft.value.typeHe = customKey;
    }
    return;
  }
  createDraft.value.typeEn = typeof preset.typeLabels?.en === 'string' ? preset.typeLabels.en : '';
  createDraft.value.typeHe = typeof preset.typeLabels?.he === 'string' ? preset.typeLabels.he : '';
}

function onEntityPresetSelected(preset) {
  if (preset === null) {
    return;
  }
  if (preset === undefined) {
    return;
  }
  if (typeof preset !== 'object') {
    return;
  }
  if (typeof preset.icon === 'string' && preset.icon.trim() !== '') {
    createDraft.value.icon = preset.icon.trim();
  }
  if (preset.nameSuggestions !== undefined && preset.nameSuggestions !== null && typeof preset.nameSuggestions === 'object') {
    if (typeof preset.nameSuggestions.en === 'string' && preset.nameSuggestions.en.trim() !== '' && createDraft.value.nameEn.trim() === '') {
      createDraft.value.nameEn = preset.nameSuggestions.en.trim();
    }
    if (typeof preset.nameSuggestions.he === 'string' && preset.nameSuggestions.he.trim() !== '' && createDraft.value.nameHe.trim() === '') {
      createDraft.value.nameHe = preset.nameSuggestions.he.trim();
    }
  }
  if (preset.typeLabels !== undefined && preset.typeLabels !== null && typeof preset.typeLabels === 'object') {
    createDraft.value.typeEn = typeof preset.typeLabels.en === 'string' ? preset.typeLabels.en : '';
    createDraft.value.typeHe = typeof preset.typeLabels.he === 'string' ? preset.typeLabels.he : '';
  }
}

function onEntityIconChange(value) {
  createDraft.value.icon = typeof value === 'string' ? value : '';
}

function getLocalizedEntries(localizedMap) {
  if (localizedMap === null) {
    return [];
  }
  if (localizedMap === undefined) {
    return [];
  }
  if (typeof localizedMap !== 'object') {
    return [];
  }
  if (Array.isArray(localizedMap) === true) {
    return [];
  }
  const entries = [];
  const keys = Object.keys(localizedMap);
  for (let index = 0; index < keys.length; index = index + 1) {
    const code = normalizeStringValue(keys[index]);
    const label = normalizeStringValue(localizedMap[keys[index]]);
    if (code === '') {
      continue;
    }
    if (label === '') {
      continue;
    }
    entries.push({ code: code, label: label });
  }
  return entries;
}

function canDeleteEntity(entity) {
  if (entity === null) {
    return false;
  }
  if (entity === undefined) {
    return false;
  }
  if (entity.isDefault === true) {
    return false;
  }
  return true;
}

function isAnalysisEnabled(entity) {
  if (entity === null) {
    return false;
  }
  if (entity === undefined) {
    return false;
  }
  const engine = entity.analysisEngine;
  if (engine === null) {
    return false;
  }
  if (engine === undefined) {
    return false;
  }
  if (typeof engine !== 'object') {
    return false;
  }
  if (engine.enabled === false) {
    return false;
  }
  if (Array.isArray(engine.selectedItems) === true && engine.selectedItems.length > 0) {
    return true;
  }
  if (Array.isArray(engine.selectedStandardItems) === true && engine.selectedStandardItems.length > 0) {
    return true;
  }
  if (Array.isArray(engine.customItems) === true && engine.customItems.length > 0) {
    return true;
  }
  return false;
}

function selectEntity(entityId) {
  selectedEntityId.value = entityId;
}

function selectTab(tabValue) {
  if (tabValue !== 'entities' && tabValue !== 'collections') {
    return;
  }
  const nextQuery = { ...route.query };
  if (tabValue === 'collections') {
    nextQuery.tab = 'collections';
  } else {
    delete nextQuery.tab;
  }
  router.replace({
    path: '/entities',
    query: nextQuery
  });
}

function resetVisibleEntities() {
  visibleEntitiesCount.value = 30;
}

function loadMoreEntities() {
  if (hasMoreEntitiesToShow.value !== true) {
    return;
  }
  visibleEntitiesCount.value = visibleEntitiesCount.value + 30;
}

function handleListScroll(event) {
  const currentTarget = event !== null && event !== undefined ? event.target : null;
  if (currentTarget === null) {
    return;
  }
  const remaining = currentTarget.scrollHeight - currentTarget.scrollTop - currentTarget.clientHeight;
  if (remaining <= 120) {
    loadMoreEntities();
  }
}

async function scrollListToTop() {
  await nextTick();
  if (listScrollRef.value !== null) {
    listScrollRef.value.scrollTop = 0;
  }
}

async function loadEntities() {
  isLoading.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/entitiesAnalysis/entities`, { headers: getAuthHeaders() });
    const list = response.data !== undefined && response.data !== null && Array.isArray(response.data.entities) === true ? response.data.entities : [];
    entities.value = list;
    resetVisibleEntities();
    if (selectedEntityId.value !== '') {
      let stillExists = false;
      for (let index = 0; index < list.length; index = index + 1) {
        if (list[index].entityId === selectedEntityId.value) {
          stillExists = true;
          break;
        }
      }
      if (stillExists === false) {
        selectedEntityId.value = '';
      }
    }
    const routeEntityId = normalizeStringValue(route.query?.entityId);
    if (routeEntityId !== '') {
      selectEntity(routeEntityId);
      return;
    }
    if (selectedEntityId.value === '' && list.length > 0) {
      selectEntity(list[0].entityId);
    }
  } catch (error) {
    toast.error(t('entities.messages.loadError', 'Failed to load entities.'));
    entities.value = [];
  } finally {
    isLoading.value = false;
  }
}

function openCreateModal() {
  createDraft.value = {
    entityId: '',
    entityType: '',
    icon: 'tabler:headset',
    nameEn: '',
    nameHe: '',
    typeEn: '',
    typeHe: ''
  };
  createModalOpen.value = true;
}

function closeCreateModal() {
  createModalOpen.value = false;
}

function buildLocalizedPayload(enValue, heValue) {
  const payload = {};
  const enText = normalizeStringValue(enValue);
  const heText = normalizeStringValue(heValue);
  if (enText !== '') {
    payload.en = enText;
  }
  if (heText !== '') {
    payload.he = heText;
  }
  return payload;
}

function openDeleteModal() {
  if (selectedEntity.value === null) {
    return;
  }
  if (canDeleteEntity(selectedEntity.value) === false) {
    toast.error(t('entities.messages.deleteDefaultBlocked', 'Default entities cannot be deleted.'));
    return;
  }
  entityPendingDelete.value = selectedEntity.value;
  deleteModalOpen.value = true;
}

function closeDeleteModal() {
  if (isDeleting.value === true) {
    return;
  }
  deleteModalOpen.value = false;
  entityPendingDelete.value = null;
}

async function deleteEntity() {
  if (entityPendingDelete.value === null) {
    return;
  }
  const entityId = typeof entityPendingDelete.value.entityId === 'string' ? entityPendingDelete.value.entityId.trim() : '';
  if (entityId === '') {
    return;
  }
  isDeleting.value = true;
  try {
    await axios.delete(`${globalState.apiEndpoint}/api/entitiesAnalysis/entities/${encodeURIComponent(entityId)}`, { headers: getAuthHeaders() });
    const deletedId = entityId;
    deleteModalOpen.value = false;
    entityPendingDelete.value = null;
    if (selectedEntityId.value === deletedId) {
      selectedEntityId.value = '';
    }
    await loadEntities();
    toast.success(t('entities.messages.deleteSuccess', 'Entity deleted.'));
  } catch (error) {
    const responseMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message.trim() : '';
    if (responseMessage !== '') {
      toast.error(responseMessage);
    } else {
      toast.error(t('entities.messages.deleteError', 'Failed to delete entity.'));
    }
  } finally {
    isDeleting.value = false;
  }
}

async function createEntity() {
  const entityType = normalizeEntityTypeKey(createDraft.value.entityType);
  const name = buildLocalizedPayload(createDraft.value.nameEn, createDraft.value.nameHe);
  if (entityType === '') {
    toast.error(t('entities.messages.typeRequired', 'Type key is required.'));
    return;
  }
  if (Object.keys(name).length === 0) {
    toast.error(t('entities.messages.nameRequired', 'At least one name is required.'));
    return;
  }
  let typeLabels = buildLocalizedPayload(createDraft.value.typeEn, createDraft.value.typeHe);
  if (Object.keys(typeLabels).length === 0) {
    const preset = findEntityTypePreset(entityType);
    if (preset !== null && preset.typeLabels !== undefined && preset.typeLabels !== null) {
      typeLabels = { ...preset.typeLabels };
    } else {
      typeLabels = { en: entityType };
    }
  }
  const entityId = normalizeStringValue(createDraft.value.entityId);
  const createPayload = {
    entityType: entityType,
    icon: resolveEntityIconValue(createDraft.value.icon),
    name: name,
    typeLabels: typeLabels
  };
  if (entityId !== '') {
    createPayload.entityId = entityId;
  }
  isCreating.value = true;
  try {
    const response = await axios.post(`${globalState.apiEndpoint}/api/entitiesAnalysis/entities`, createPayload, { headers: getAuthHeaders() });
    closeCreateModal();
    await loadEntities();
    if (response.data !== undefined && response.data !== null && response.data.entity !== undefined && response.data.entity !== null) {
      const createdId = typeof response.data.entity.entityId === 'string' ? response.data.entity.entityId : '';
      if (createdId !== '') {
        selectEntity(createdId);
      }
    }
    toast.success(t('entities.messages.createSuccess', 'Entity created.'));
  } catch (error) {
    toast.error(t('entities.messages.createError', 'Failed to create entity.'));
  } finally {
    isCreating.value = false;
  }
}

onMounted(async () => {
  await loadEntities();
});

watch(searchValue, async () => {
  resetVisibleEntities();
  await scrollListToTop();
});

watch(filteredEntities, (list) => {
  if (Array.isArray(list) !== true || list.length === 0) {
    selectedEntityId.value = '';
    return;
  }
  let exists = false;
  for (let index = 0; index < list.length; index = index + 1) {
    if (list[index].entityId === selectedEntityId.value) {
      exists = true;
      break;
    }
  }
  if (exists !== true) {
    selectedEntityId.value = list[0].entityId;
  }
});
</script>

<style scoped>
.entities-page {
  width: 98%;
  margin: 1.5rem auto 0 auto;
}

.entities-page-intro {
  margin: 0.5rem 0.75rem 0 0.75rem;
}

.entities-page-rtl .entities-page-intro {
  text-align: right;
}

.entities-page-tabs-shell {
  margin: 0.75rem 0.75rem 0 0.75rem;
}

.entities-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.5rem 0.75rem 1rem 0.75rem;
}

.entities-page-rtl .entities-header-intro {
  text-align: right;
}

.entities-layout {
  display: grid;
  grid-template-columns: minmax(16rem, 22rem) minmax(0, 1fr);
  margin: 0 0.75rem 1.5rem 0.75rem;
}

.entities-list-card,
.entities-detail-card {
  min-height: 24rem;
}

.entities-list-card {
  margin-inline-end: 0.75rem;
  display: flex;
  flex-direction: column;
}

.entities-list-toolbar {
  padding: 1rem 1rem 0.5rem 1rem;
}

.entities-search-input {
  width: 100%;
}

.entities-list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 72vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.entities-state {
  min-height: 20rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  color: #6b7280;
  text-align: center;
}

.entities-list {
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
}

.entities-list-loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem 1rem 1rem;
  color: #6b7280;
}

.entities-list-loading-more > *:not(:last-child) {
  margin-inline-end: 0.5rem;
}

.entities-list-item {
  display: flex;
  align-items: flex-start;
  width: 100%;
  text-align: start;
  padding: 0.875rem 1rem;
  margin: 0.25rem 0;
  border: 1px solid rgba(229, 231, 235, 0.9);
  border-radius: 0.75rem;
  background: rgba(249, 250, 251, 0.85);
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.entities-list-item:hover {
  border-color: rgba(99, 102, 241, 0.45);
}

.entities-list-item-active {
  border-color: rgba(79, 70, 229, 0.75);
  background: rgba(238, 242, 255, 0.95);
}

.entities-list-item-icon {
  flex-shrink: 0;
  color: #4f46e5;
  margin-inline-end: 0.75rem;
}

.entities-list-item-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.entities-list-item-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: -0.125rem;
}

.entities-list-item-title-row > * {
  margin: 0.125rem;
}

.entities-detail {
  padding: 1.25rem 1.5rem;
}

.entities-detail-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin: -0.375rem;
}

.entities-detail-header > * {
  margin: 0.375rem;
}

.entities-detail-icon {
  flex-shrink: 0;
  color: #4f46e5;
}

.entities-detail-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.entities-page-rtl .entities-detail-heading {
  text-align: right;
}

.entities-detail-actions {
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  margin: -0.25rem;
}

.entities-detail-actions > * {
  margin: 0.25rem;
}

.entities-delete-modal-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: -0.25rem;
}

.entities-delete-modal-actions > * {
  margin: 0.25rem;
}

.entities-delete-modal-actions-rtl {
  justify-content: flex-start;
}

.entities-delete-spinner {
  margin-inline-end: 0.5rem;
}

.entities-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  margin: 1.25rem -0.5rem 0 -0.5rem;
}

.entities-detail-field {
  margin: 0.5rem;
}

.entities-detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.entities-detail-value {
  margin-top: 0.35rem;
  font-size: 0.9375rem;
  color: #111827;
}

.dark .entities-detail-value {
  color: #f3f4f6;
}

.entities-detail-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8125rem;
  word-break: break-all;
}

.entities-localized-section {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(229, 231, 235, 0.9);
}

.entities-localized-list {
  margin-top: 0.75rem;
}

.entities-localized-row {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(243, 244, 246, 0.9);
}

.entities-localized-code {
  flex-shrink: 0;
  min-width: 2.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #6b7280;
  margin-inline-end: 0.75rem;
}

.entities-localized-text {
  flex: 1 1 auto;
  font-size: 0.9375rem;
  color: #111827;
}

.dark .entities-localized-text {
  color: #f3f4f6;
}

.entities-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  margin: -0.5rem;
}

.entities-form-grid > * {
  margin: 0.5rem;
}

:global(.modal-box.entities-create-modal-box) {
  display: flex;
  flex-direction: column;
  max-height: min(90vh, 44rem);
  overflow: hidden;
}

.entities-modal-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.entities-modal-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: -0.25rem;
}

.entities-modal-actions > * {
  margin: 0.25rem;
}

.entities-modal-actions-rtl {
  justify-content: flex-start;
}

.entities-save-spinner {
  margin-inline-end: 0.5rem;
}

@media (max-width: 900px) {
  .entities-layout {
    grid-template-columns: 1fr;
  }

  .entities-list-card {
    margin-inline-end: 0;
    margin-bottom: 0.75rem;
  }
}
</style>
